import React, { useState, useRef } from 'react';
import { 
  Building2, Mail, Lock, Upload, ChevronRight, Check, 
  Globe, Sparkles, LayoutDashboard, Settings, 
  Users, Gift, ArrowRight, Crown, Phone 
} from 'lucide-react';

// IMPORTACIONES DE FIREBASE
import { db, storage } from './firebase';
import { collection, doc, setDoc, addDoc, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

export default function App() {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dashboardTab, setDashboardTab] = useState('panel'); 
  const fileInputRef = useRef(null);
  
  // Guardamos el archivo físico del logo si el usuario sube uno
  const [logoFile, setLogoFile] = useState(null);

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    hotelName: '',
    currency: 'USD',
    plan: 'Premium',
    template: 'Urbano',
    logoUrl: 'https://images.paxfidely.com/placeholders/logo-default.png' // Imagen por defecto
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Manejador para previsualizar el logo localmente y guardar el archivo
  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file); // Guardamos el archivo para subirlo después a Firebase
      const imageUrl = URL.createObjectURL(file); // Creamos la URL temporal para previsualizar
      setFormData(prev => ({ ...prev, logoUrl: imageUrl }));
    }
  };

  // ==========================================
  // FUNCIONES DE INTEGRACIÓN (AHORA REALES CON FIREBASE)
  // ==========================================

    const provisionFirebaseEnvironment = async (data) => {
    try {
      // 1. Crear el ID del hotel (Ej: hotel-las-palmas) y el ID de su "Isla Privada"
      const hotelId = data.hotelName.toLowerCase().replace(/[^a-z0-9]/g, '-');
      
      // CIRUGÍA LÁSER: Validar si el Hotel ya existe para evitar clonaciones
      const hotelRef = doc(db, 'hotels', hotelId);
      const hotelSnap = await getDoc(hotelRef);
      if (hotelSnap.exists()) {
          throw new Error("Ya existe un hotel registrado con este nombre. Por favor, agregue la ciudad o sucursal para diferenciarlo (Ej: Hotel Las Palmas Centro).");
      }

      const tenantAppId = `tenant-${hotelId}`; // <--- ESTA ES LA ISLA PRIVADA
      let finalLogoUrl = data.logoUrl;

      // 2. Subir logo a Firebase Storage si subió uno
      if (logoFile) {
        const logoRef = ref(storage, `hotel_logos/${hotelId}_${logoFile.name}`);
        await uploadBytes(logoRef, logoFile);
        finalLogoUrl = await getDownloadURL(logoRef);
      }

      // 3. Guardar el hotel en el directorio "maestro" de PaxFidely
      const hotelData = {
        hotelId: hotelId,
        adminName: data.fullName,
        adminEmail: data.email,
        hotelName: data.hotelName,
        currency: data.currency,
        plan: data.plan,
        template: data.template,
        logoUrl: finalLogoUrl,
        createdAt: new Date().toISOString(),
        status: 'Trial Active'
      };
      await setDoc(doc(db, 'hotels', hotelId), hotelData);

      // 4. CONFIGURAR LA ISLA PRIVADA DEL HOTEL (Diseño y Niveles)
      await setDoc(doc(db, 'artifacts', tenantAppId, 'public', 'data', 'settings', 'config'), {
         // INYECCIÓN DINÁMICA: Enterprise arranca como Cadena, el resto como Independiente
         isChainMode: data.plan === 'Enterprise', 
         isSetupLocked: false, // El candado empieza abierto para que el Admin lo confirme en el Hub
         hotelName: data.hotelName, 
         chainName: data.hotelName,
         logo: finalLogoUrl,
         primaryColor: data.template === 'Boutique' ? '#C5A059' : (data.template === 'Urbano' ? '#3b82f6' : '#10b981'),
         welcomePoints: 500, 
         selectedLanguage: 'Español',
         tiers: [
             { id: 't1', active: true, name: 'Classic', cashback: 2, color: '#9ca3af' },
             { id: 't2', active: true, name: 'Gold', cashback: 4, color: '#fbbf24' },
             { id: 't3', active: true, name: 'Platinum', cashback: 6, color: '#60a5fa' },
             { id: 't4', active: true, name: 'Select', cashback: 8, color: '#18181b' }
         ]
      });

      // 5. INYECTAR CATÁLOGO DE BENEFICIOS SEGÚN LA PLANTILLA ELEGIDA
      let beneficiosTemplate = [];
      
      if (data.template === 'Boutique') {
         beneficiosTemplate = [
            { title: 'Late Check-out', cost: 1000, desc: 'Extensión de salida hasta las 14:00hs.', stock: 99, hotelId: 'Todos', image: 'https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&q=80&w=500' },
            { title: 'Desayuno en Habitación', cost: 1500, desc: 'Servicio continental privado a la habitación.', stock: 50, hotelId: 'Todos', image: 'https://images.unsplash.com/photo-1533090161767-e6ffed986c88?auto=format&fit=crop&q=80&w=500' }
         ];
      } else if (data.template === 'Urbano') {
         beneficiosTemplate = [
            { title: 'Upgrade de Categoría', cost: 5000, desc: 'Sujeto a disponibilidad al momento del check-in.', stock: 10, hotelId: 'Todos', image: 'https://images.unsplash.com/photo-1578683010236-d716f9a3f461?auto=format&fit=crop&q=80&w=500' },
            { title: 'Desayuno Ejecutivo', cost: 2500, desc: 'Acceso al buffet premium para una persona.', stock: 50, hotelId: 'Todos', image: 'https://images.unsplash.com/photo-1495147466023-ac5c588e2e94?auto=format&fit=crop&q=80&w=500' }
         ];
      } else { // Vacacional / Enterprise
         beneficiosTemplate = [
            { title: 'Cena de Cortesía', cost: 6000, desc: 'Menú de 3 pasos en el restaurante exclusivo.', stock: 20, hotelId: 'Todos', image: 'https://images.unsplash.com/photo-1544148103-0773bf10d330?auto=format&fit=crop&q=80&w=500' },
            { title: 'Acceso Circuito Spa', cost: 3000, desc: 'Pase de 2 horas para áreas de relajación y sauna.', stock: 50, hotelId: 'Todos', image: 'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?auto=format&fit=crop&q=80&w=500' }
         ];
      }

      // 6. Guardamos cada beneficio inyectado en la base de datos aislada
      for (const benefit of beneficiosTemplate) {
         await addDoc(collection(db, 'artifacts', tenantAppId, 'public', 'data', 'benefits'), {
             ...benefit, 
             createdAt: new Date().toISOString()
         });
      }

      console.log(`[FIREBASE] Entorno aislado creado exitosamente para: ${tenantAppId}`);

    } catch (error) {
      console.error("[FIREBASE ERROR] Ocurrió un error al aprovisionar:", error);
      throw error; // Lanza el error para frenar la pantalla de carga
    }
  };

 const nextStep = () => {
    setStep(s => s + 1);
  };

  const handleFinalSubmit = async () => {
    setIsSubmitting(true);
// --- INTEGRACIÓN CRM GOOGLE SHEETS AUTOMÁTICO (VERSIÓN BLINDADA CON ETAPA) ---
    const webhookUrl = "https://script.google.com/macros/s/AKfycbwlbrKL8zppmnCesWbkQBS29PjIzsMiR32UNIT_RF-xBruKt908ht0OVCpVdUiAHjc/exec";

    const leadData = {
      hotelName: formData.hotelName || "",
      adminName: formData.fullName || "",
      email: formData.email || "",
      phone: formData.phone || "", // <-- AHORA CAPTURA EL DATO DEL FORMULARIO
      plan: formData.plan || "Enterprise", 
      etapa: "Trial 14 Días"
    };

    // Usamos text/plain para evadir el bloqueo de seguridad (CORS) del navegador
    fetch(webhookUrl, {
      method: "POST",
      mode: "no-cors",
      headers: { 
        "Content-Type": "text/plain;charset=utf-8" 
      },
      body: JSON.stringify(leadData)
    })
    .then(() => console.log("Se disparó el Webhook hacia Google con etapa Trial"))
    .catch(err => console.error("Error disparando Webhook:", err));
    // -------------------------------------------------------------------        
    try {
      // Ejecutamos la creación real en Firebase
      await provisionFirebaseEnvironment(formData);
          
      setIsSubmitting(false);
      setStep(3); // Salta al Dashboard (Momento Wow)

    } catch (error) {
      console.error("Error en la creación de la plataforma:", error);
      alert("Hubo un error al configurar su plataforma. Por favor, intente de nuevo.");
      setIsSubmitting(false);
    }
  };

  // ==========================================
  // COMPONENTES DE LA INTERFAZ
  // ==========================================

  const renderProgressBar = () => (
    <div className="w-full max-w-3xl mx-auto mb-12">
      <div className="flex items-center justify-between relative">
        <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-full h-1 bg-neutral-800 z-0 rounded-full"></div>
        <div 
          className="absolute left-0 top-1/2 transform -translate-y-1/2 h-1 bg-amber-500 z-0 rounded-full transition-all duration-500"
          style={{ width: `${((step - 1) / 2) * 100}%` }}
        ></div>
        
        {['Cuenta', 'Personalización', 'Activación'].map((label, index) => {
          const stepNum = index + 1;
          const isActive = step >= stepNum;
          const isCurrent = step === stepNum;
          
          return (
            <div key={label} className="relative z-10 flex flex-col items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all duration-300 ${
                isActive ? 'bg-amber-500 text-neutral-950' : 'bg-neutral-900 text-neutral-500 border border-neutral-700'
              } ${isCurrent ? 'ring-4 ring-amber-500/20' : ''}`}>
                {step > stepNum ? <Check size={20} /> : stepNum}
              </div>
              <span className={`mt-2 text-xs font-medium uppercase tracking-wider ${isActive ? 'text-amber-500' : 'text-neutral-500'}`}>
                {label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div className="animate-fade-in-up w-full max-w-md mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-light text-white mb-2">Comencemos su <span className="font-bold text-amber-500">Prueba Gratuita</span></h2>
        <p className="text-neutral-400">Configure su plataforma en menos de 2 minutos. Sin tarjeta de crédito.</p>
      </div>

      <div className="space-y-4">
        
        {/* NUEVO SELECTOR: ESTRUCTURA OPERATIVA */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-neutral-400 mb-3">Estructura Operativa</label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button 
              type="button"
              onClick={() => setFormData({...formData, plan: 'Premium'})}
              className={`p-4 rounded-xl border text-left transition-all ${formData.plan !== 'Enterprise' ? 'bg-amber-500/10 border-amber-500 text-amber-500 shadow-[0_0_15px_rgba(245,158,11,0.15)]' : 'bg-neutral-900/50 border-neutral-800 text-neutral-400 hover:border-neutral-600'}`}
            >
              <div className="font-bold mb-1 text-sm">Sede Independiente</div>
              <div className="text-[9px] uppercase tracking-widest opacity-80">Gestión de 1 solo hotel</div>
            </button>
            
            <button 
              type="button"
              onClick={() => setFormData({...formData, plan: 'Enterprise'})}
              className={`p-4 rounded-xl border text-left transition-all ${formData.plan === 'Enterprise' ? 'bg-amber-500/10 border-amber-500 text-amber-500 shadow-[0_0_15px_rgba(245,158,11,0.15)]' : 'bg-neutral-900/50 border-neutral-800 text-neutral-400 hover:border-neutral-600'}`}
            >
              <div className="font-bold mb-1 text-sm">Cadena Hotelera</div>
              <div className="text-[9px] uppercase tracking-widest opacity-80">Red de múltiples sedes</div>
            </button>
          </div>
        </div>

        {/* EL VENDEDOR SILENCIOSO (GAMIFICACIÓN DE VENTAS) */}
        {formData.plan === 'Enterprise' && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-5 mb-6 animate-in fade-in slide-in-from-top-2 shadow-inner">
            <h4 className="text-amber-500 text-[10px] font-bold uppercase tracking-widest flex items-center gap-2 mb-2">
              <Sparkles size={14} /> Módulo Pionero Activado
            </h4>
            <p className="text-amber-400/80 text-xs font-light leading-relaxed">
              Está configurando el Hub Central de su cadena de forma gratuita. Al sumar una segunda sede operativa a su red desde el panel de control, <strong>desbloqueará automáticamente la Tarifa Mayorista</strong> para todos sus franquiciados o sedes propias.
            </p>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-neutral-400 mb-1">Nombre del Responsable</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-neutral-500">
              <Users size={18} />
            </div>
            <input type="text" name="fullName" value={formData.fullName} onChange={handleInputChange} className="block w-full pl-10 pr-3 py-3 border border-neutral-800 bg-neutral-900/50 rounded-lg text-white focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors" placeholder="Ej. Carlos Martínez" />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-neutral-400 mb-1">Email Corporativo</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-neutral-500">
              <Mail size={18} />
            </div>
            <input type="email" name="email" value={formData.email} onChange={handleInputChange} className="block w-full pl-10 pr-3 py-3 border border-neutral-800 bg-neutral-900/50 rounded-lg text-white focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors" placeholder="carlos@suhotel.com" />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-neutral-400 mb-1">WhatsApp de Contacto</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-neutral-500">
              <Phone size={18} />
            </div>
            <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} className="block w-full pl-10 pr-3 py-3 border border-neutral-800 bg-neutral-900/50 rounded-lg text-white focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors" placeholder="+54 9 11..." />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-neutral-400 mb-1">Contraseña</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-neutral-500">
              <Lock size={18} />
            </div>
            <input type="password" name="password" value={formData.password} onChange={handleInputChange} className="block w-full pl-10 pr-3 py-3 border border-neutral-800 bg-neutral-900/50 rounded-lg text-white focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors" placeholder="••••••••" />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-neutral-400 mb-1">Nombre del Hotel / Cadena</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-neutral-500">
              <Building2 size={18} />
            </div>
            <input type="text" name="hotelName" value={formData.hotelName} onChange={handleInputChange} className="block w-full pl-10 pr-3 py-3 border border-neutral-800 bg-neutral-900/50 rounded-lg text-white focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors" placeholder="Ej. Hotel Amérian" />
          </div>
        </div>

        <button 
          onClick={nextStep}
          disabled={!formData.fullName || !formData.email || !formData.hotelName}
          className="w-full mt-6 bg-amber-500 hover:bg-amber-400 text-neutral-950 font-bold py-3 px-4 rounded-lg flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Continuar <ChevronRight size={20} className="ml-2" />
        </button>
      </div>
    </div>
  );

  const renderStep2 = () => {
    const isDefaultLogo = formData.logoUrl === 'https://images.paxfidely.com/placeholders/logo-default.png';
    
    return (
      <div className="animate-fade-in-up w-full max-w-4xl mx-auto flex flex-col md:flex-row gap-12 items-start">
        <div className="flex-1 w-full space-y-6">
          <div className="mb-6">
            <h2 className="text-3xl font-light text-white mb-2">Identidad de <span className="font-bold text-amber-500">Marca</span></h2>
            <p className="text-neutral-400">Personalice la plataforma para que sus huéspedes reconozcan su sello.</p>
          </div>

          {/* Selector de Archivo Oculto */}
          <input 
            type="file" 
            ref={fileInputRef} 
            onChange={handleLogoUpload} 
            className="hidden" 
            accept="image/png, image/jpeg, image/svg+xml" 
          />
          
          {/* Área de carga interactiva */}
          <div 
            onClick={() => fileInputRef.current?.click()}
            className="p-6 border border-neutral-800 bg-neutral-900/30 rounded-xl border-dashed hover:border-amber-500/50 transition-colors text-center cursor-pointer group"
          >
            {isDefaultLogo ? (
              <div className="w-16 h-16 bg-neutral-800 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-amber-500/10 transition-colors">
                <Upload size={24} className="text-neutral-400 group-hover:text-amber-500 transition-colors" />
              </div>
            ) : (
              <img src={formData.logoUrl} alt="Logo Subido" className="h-20 mx-auto mb-4 object-contain" />
            )}
            
            <h3 className="text-white font-medium mb-1">
              {isDefaultLogo ? 'Subir Logo del Hotel' : 'Cambiar Logo'}
            </h3>
            <p className="text-neutral-500 text-sm mb-4">PNG o JPG transparente recomendado (Max. 2MB)</p>
            <button className="text-amber-500 text-sm font-bold border border-amber-500/30 px-4 py-2 rounded-lg group-hover:bg-amber-500/10 transition-colors">
              Examinar Archivos
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-400 mb-1">Moneda de Operación</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-neutral-500">
                <Globe size={18} />
              </div>
              <select name="currency" value={formData.currency} onChange={handleInputChange} className="block w-full pl-10 pr-3 py-3 border border-neutral-800 bg-neutral-900/50 rounded-lg text-white focus:ring-2 focus:ring-amber-500 focus:border-amber-500 appearance-none">
                <option value="USD" className="bg-neutral-900 text-white">Dólar Estadounidense (USD)</option>
                <option value="EUR" className="bg-neutral-900 text-white">Euro (EUR)</option>
                <option value="ARS" className="bg-neutral-900 text-white">Peso Argentino (ARS)</option>
                <option value="MXN" className="bg-neutral-900 text-white">Peso Mexicano (MXN)</option>
              </select>
            </div>
          </div>

          <div className="flex gap-4 pt-4">
            <button onClick={() => setStep(1)} className="px-6 py-3 border border-neutral-800 text-white rounded-lg hover:bg-neutral-800 transition-colors">Atrás</button>
            <button 
              onClick={handleFinalSubmit} 
              disabled={isSubmitting}
              className="flex-1 bg-amber-500 hover:bg-amber-400 text-neutral-950 font-bold py-3 px-4 rounded-lg flex items-center justify-center transition-colors disabled:opacity-70"
            >
              {isSubmitting ? 'Construyendo...' : 'Activar Plataforma'} <ChevronRight size={20} className="ml-2" />
            </button>
          </div>
        </div>

        {/* Live Preview Mockup */}
        <div className="flex-1 w-full max-w-sm hidden md:block">
          <div className="relative mx-auto border-8 border-neutral-800 rounded-[2.5rem] h-[600px] w-[280px] bg-neutral-950 overflow-hidden shadow-2xl">
            <div className="absolute top-0 inset-x-0 h-6 bg-neutral-800 rounded-b-3xl mx-16 z-10"></div>
            {/* App Preview Content */}
            <div className="h-full w-full flex flex-col pt-12 pb-6 px-6 bg-gradient-to-b from-neutral-900 to-black overflow-y-auto">
               <div className="flex justify-center mb-8 h-8 items-center">
                 {isDefaultLogo ? (
                   <div className="text-amber-500 font-bold text-xl tracking-widest uppercase border-b-2 border-amber-500 pb-1 text-center truncate w-full">
                     {formData.hotelName || 'SU HOTEL'}
                   </div>
                 ) : (
                   <img src={formData.logoUrl} alt="Logo" className="max-h-8 object-contain" />
                 )}
               </div>
               <div className="bg-neutral-800/50 rounded-2xl p-5 mb-4 border border-neutral-700/50 backdrop-blur-sm">
                 <p className="text-neutral-400 text-xs uppercase tracking-wider mb-1">Su Nivel</p>
                 <h4 className="text-white font-bold text-lg">Miembro Gold</h4>
                 <div className="mt-4 pt-4 border-t border-neutral-700 flex justify-between items-center">
                   <span className="text-neutral-400 text-sm">Puntos Disponibles</span>
                   <span className="text-amber-500 font-bold text-xl">12,500</span>
                 </div>
               </div>
               <h5 className="text-white text-sm font-medium mb-3 mt-4">Catálogo de Beneficios</h5>
               <div className="space-y-3">
                  <div className="h-16 bg-neutral-800/80 rounded-xl animate-pulse"></div>
                  <div className="h-16 bg-neutral-800/80 rounded-xl animate-pulse"></div>
               </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderStep3 = () => (
    <div className="animate-fade-in-up w-full max-w-5xl mx-auto">
      <div className="text-center mb-10">
        <h2 className="text-3xl font-light text-white mb-2">Elija su <span className="font-bold text-amber-500">Plan</span></h2>
        <p className="text-neutral-400">Seleccione el plan que mejor se adapte a su propiedad. <br/><span className="text-amber-500">Podrá cambiar de plan en cualquier momento durante sus 14 días de prueba.</span></p>
      </div>

      <div className="grid md:grid-cols-3 gap-6 mb-8">
        {/* Plan Boutique */}
        <div 
          onClick={() => setFormData({...formData, template: 'Boutique', plan: 'Boutique'})}
          className={`cursor-pointer rounded-2xl p-6 border-2 transition-all relative overflow-hidden flex flex-col ${formData.template === 'Boutique' ? 'border-amber-500 bg-amber-500/5 shadow-[0_0_30px_rgba(245,158,11,0.15)]' : 'border-neutral-800 bg-neutral-900/30 hover:border-neutral-600'}`}
        >
          {formData.template === 'Boutique' && <div className="absolute top-0 right-0 bg-amber-500 text-neutral-950 text-[10px] font-bold px-3 py-1 rounded-bl-lg">SELECCIONADO</div>}
          <div className="text-center mb-4 border-b border-neutral-800 pb-4">
             <h3 className="text-lg font-bold text-white uppercase tracking-wider mb-1">Boutique</h3>
             <p className="text-neutral-500 text-xs h-8">Para posadas y hoteles independientes que dan su primer paso digital.</p>
             <div className="text-3xl text-amber-500 font-bold mt-4">$79.00</div>
             <div className="text-neutral-500 text-xs">por mes post-prueba</div>
          </div>
          <ul className="space-y-3 text-sm text-neutral-300 flex-1">
            <li className="flex items-center justify-center text-xs text-neutral-500 uppercase tracking-wider mb-2">Detalle de funciones:</li>
            <li className="flex items-start"><Check size={16} className="text-amber-500 mr-2 mt-0.5 shrink-0" /> 1 Sede única</li>
            <li className="flex items-start"><Check size={16} className="text-amber-500 mr-2 mt-0.5 shrink-0" /> Hasta 1.000 socios</li>
            <li className="flex items-start"><Check size={16} className="text-amber-500 mr-2 mt-0.5 shrink-0" /> Catálogo base (5 premios/beneficios)</li>
            <li className="flex items-start"><Check size={16} className="text-amber-500 mr-2 mt-0.5 shrink-0" /> Chat conserjería integrado</li>
          </ul>
        </div>

        {/* Plan Premium */}
        <div 
          onClick={() => setFormData({...formData, template: 'Premium', plan: 'Premium'})}
          className={`cursor-pointer rounded-2xl p-6 border-2 transition-all relative overflow-hidden flex flex-col ${formData.template === 'Premium' ? 'border-amber-500 bg-amber-500/5 shadow-[0_0_30px_rgba(245,158,11,0.15)]' : 'border-neutral-800 bg-neutral-900/30 hover:border-neutral-600'}`}
        >
          {formData.template === 'Premium' && <div className="absolute top-0 right-0 bg-amber-500 text-neutral-950 text-[10px] font-bold px-3 py-1 rounded-bl-lg">SELECCIONADO</div>}
          <div className="text-center mb-4 border-b border-neutral-800 pb-4">
             <h3 className="text-lg font-bold text-white uppercase tracking-wider mb-1">Premium</h3>
             <p className="text-neutral-500 text-xs h-8">El plan ideal para hoteles de 4 y 5 estrellas con alta ocupación.</p>
             <div className="text-3xl text-amber-500 font-bold mt-4">$149.00</div>
             <div className="text-neutral-500 text-xs">por mes post-prueba</div>
          </div>
          <ul className="space-y-3 text-sm text-neutral-300 flex-1">
             <li className="flex items-center justify-center text-xs text-neutral-500 uppercase tracking-wider mb-2">Nivel anterior más:</li>
            <li className="flex items-start"><Check size={16} className="text-amber-500 mr-2 mt-0.5 shrink-0" /> Socios Ilimitados</li>
            <li className="flex items-start"><Check size={16} className="text-amber-500 mr-2 mt-0.5 shrink-0" /> Catálogo Ilimitado</li>
            <li className="flex items-start"><Check size={16} className="text-amber-500 mr-2 mt-0.5 shrink-0" /> Módulo CRM Preferencial</li>
            <li className="flex items-start"><Check size={16} className="text-amber-500 mr-2 mt-0.5 shrink-0" /> Campañas Marketing Push</li>
          </ul>
        </div>

        {/* Plan Enterprise */}
        <div 
          onClick={() => setFormData({...formData, template: 'Enterprise', plan: 'Enterprise'})}
          className={`cursor-pointer rounded-2xl p-6 border-2 transition-all relative overflow-hidden flex flex-col ${formData.template === 'Enterprise' ? 'border-amber-500 bg-amber-500/5 shadow-[0_0_30px_rgba(245,158,11,0.15)]' : 'border-neutral-800 bg-neutral-900/30 hover:border-neutral-600'}`}
        >
          {/* Badge de "Opción Más Popular" tipo cinta */}
          <div className="absolute top-0 inset-x-0 bg-amber-500 text-neutral-950 text-[10px] font-bold py-1 text-center uppercase tracking-wider">
            Opción Más Popular
          </div>
          {formData.template === 'Enterprise' && <div className="absolute top-6 right-0 bg-amber-500 text-neutral-950 text-[10px] font-bold px-3 py-1 rounded-bl-lg shadow-md z-10 border-l border-b border-amber-600">SELECCIONADO</div>}
          
          <div className="text-center mb-4 border-b border-neutral-800 pb-4 mt-6">
             <h3 className="text-lg font-bold text-white uppercase tracking-wider mb-1">Enterprise</h3>
             <p className="text-neutral-500 text-xs h-8">Para grupos hoteleros que buscan escalar sin fricciones.</p>
             <div className="text-3xl text-amber-500 font-bold mt-4">$349.00</div>
             <div className="text-neutral-500 text-xs">por mes post-prueba</div>
          </div>
          <ul className="space-y-3 text-sm text-neutral-300 flex-1">
             <li className="flex items-center justify-center text-xs text-neutral-500 uppercase tracking-wider mb-2">Nivel anterior más:</li>
            <li className="flex items-start"><Check size={16} className="text-amber-500 mr-2 mt-0.5 shrink-0" /> Multi-Tenant (Hasta 2 sedes)</li>
            <li className="flex items-start"><Check size={16} className="text-amber-500 mr-2 mt-0.5 shrink-0" /> Marca Blanca Total</li>
            <li className="flex items-start"><Check size={16} className="text-amber-500 mr-2 mt-0.5 shrink-0" /> Bono Reserva Directa (Anti-OTA)</li>
            <li className="flex items-start"><Check size={16} className="text-amber-500 mr-2 mt-0.5 shrink-0" /> Conexión API PMS / Webhooks</li>
          </ul>
        </div>
      </div>

      <div className="flex justify-between items-center bg-neutral-900/80 p-4 rounded-xl border border-neutral-800 mb-8">
         <div className="flex items-center text-neutral-300 text-sm">
            <Crown size={18} className="text-amber-500 mr-2" />
            Ecosistema a instalar: <span className="font-bold text-white ml-2">Plan {formData.plan} (Prueba Gratuita 14 días)</span>
         </div>
      </div>

      <div className="flex gap-4 justify-center">
        <button onClick={() => setStep(2)} className="px-6 py-4 border border-neutral-800 text-white rounded-lg hover:bg-neutral-800 transition-colors">Atrás</button>
        <button 
          onClick={handleFinalSubmit} 
          disabled={isSubmitting}
          className="w-full max-w-md bg-amber-500 hover:bg-amber-400 text-neutral-950 font-bold py-4 px-6 rounded-lg flex items-center justify-center transition-colors disabled:opacity-70 text-lg shadow-[0_0_20px_rgba(245,158,11,0.3)]"
        >
          {isSubmitting ? (
            <span className="flex items-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-neutral-950" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Creando Base de Datos...
            </span>
          ) : (
             <>Construir mi Plataforma <Sparkles size={20} className="ml-2" /></>
          )}
        </button>
      </div>
    </div>
  );

  const renderDashboardContent = () => {
    switch(dashboardTab) {
      case 'crm':
        return (
          <div className="animate-fade-in bg-neutral-900/50 border border-neutral-800 rounded-2xl p-8">
            <h2 className="text-2xl font-light text-white mb-6">Socios / CRM</h2>
            <div className="text-center py-16 text-neutral-500">
              <Users size={48} className="mx-auto mb-4 opacity-50" />
              <p className="mb-4">Aún no hay huéspedes registrados en su programa de lealtad.</p>
              <button className="bg-amber-500 hover:bg-amber-400 text-neutral-950 font-bold px-6 py-2 rounded-lg transition-colors">
                Invitar a mi primer huésped
              </button>
            </div>
          </div>
        );
      case 'catalogo':
        return (
           <div className="animate-fade-in bg-neutral-900/50 border border-neutral-800 rounded-2xl p-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-light text-white">Catálogo de Beneficios</h2>
              <span className="bg-neutral-800 text-neutral-300 px-3 py-1 rounded-full text-xs">Plantilla: {formData.template}</span>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
               {formData.template === 'Urbano' ? (
                 <>
                   <div className="p-5 border border-neutral-800 rounded-xl bg-[#0a0a0a] hover:border-amber-500/30 transition-colors">
                     <div className="w-10 h-10 bg-neutral-800 rounded-lg flex items-center justify-center text-neutral-400 mb-4"><Building2 size={20}/></div>
                     <div className="font-bold text-white mb-1">Late Check-out</div>
                     <p className="text-neutral-500 text-xs mb-3">Extensión de salida hasta las 14:00hs.</p>
                     <div className="text-amber-500 font-bold">1,000 pts</div>
                   </div>
                   <div className="p-5 border border-neutral-800 rounded-xl bg-[#0a0a0a] hover:border-amber-500/30 transition-colors">
                     <div className="w-10 h-10 bg-neutral-800 rounded-lg flex items-center justify-center text-neutral-400 mb-4"><Crown size={20}/></div>
                     <div className="font-bold text-white mb-1">Upgrade de Categoría</div>
                     <p className="text-neutral-500 text-xs mb-3">Sujeto a disponibilidad al momento del check-in.</p>
                     <div className="text-amber-500 font-bold">5,000 pts</div>
                   </div>
                   <div className="p-5 border border-neutral-800 rounded-xl bg-[#0a0a0a] hover:border-amber-500/30 transition-colors">
                     <div className="w-10 h-10 bg-neutral-800 rounded-lg flex items-center justify-center text-neutral-400 mb-4"><Gift size={20}/></div>
                     <div className="font-bold text-white mb-1">Desayuno Ejecutivo</div>
                     <p className="text-neutral-500 text-xs mb-3">Acceso al buffet premium para una persona.</p>
                     <div className="text-amber-500 font-bold">2,500 pts</div>
                   </div>
                 </>
               ) : (
                 <>
                   <div className="p-5 border border-neutral-800 rounded-xl bg-[#0a0a0a] hover:border-amber-500/30 transition-colors">
                     <div className="w-10 h-10 bg-neutral-800 rounded-lg flex items-center justify-center text-neutral-400 mb-4"><Sparkles size={20}/></div>
                     <div className="font-bold text-white mb-1">Acceso Circuito Spa</div>
                     <p className="text-neutral-500 text-xs mb-3">Pase de 2 horas para áreas de relajación y sauna.</p>
                     <div className="text-amber-500 font-bold">3,000 pts</div>
                   </div>
                   <div className="p-5 border border-neutral-800 rounded-xl bg-[#0a0a0a] hover:border-amber-500/30 transition-colors">
                     <div className="w-10 h-10 bg-neutral-800 rounded-lg flex items-center justify-center text-neutral-400 mb-4"><Gift size={20}/></div>
                     <div className="font-bold text-white mb-1">Cóctel de Bienvenida</div>
                     <p className="text-neutral-500 text-xs mb-3">Una bebida de autor en nuestro bar principal.</p>
                     <div className="text-amber-500 font-bold">800 pts</div>
                   </div>
                   <div className="p-5 border border-neutral-800 rounded-xl bg-[#0a0a0a] hover:border-amber-500/30 transition-colors">
                     <div className="w-10 h-10 bg-neutral-800 rounded-lg flex items-center justify-center text-neutral-400 mb-4"><Crown size={20}/></div>
                     <div className="font-bold text-white mb-1">Cena de Cortesía</div>
                     <p className="text-neutral-500 text-xs mb-3">Menú de 3 pasos en el restaurante exclusivo.</p>
                     <div className="text-amber-500 font-bold">6,000 pts</div>
                   </div>
                 </>
               )}
            </div>
          </div>
        );
      case 'configuracion':
        return (
           <div className="animate-fade-in bg-neutral-900/50 border border-neutral-800 rounded-2xl p-8">
            <h2 className="text-2xl font-light text-white mb-6">Configuración de Plataforma</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <h3 className="text-lg text-white font-medium border-b border-neutral-800 pb-2">Datos Generales</h3>
                <div className="flex justify-between border-b border-neutral-800/50 pb-2">
                  <span className="text-neutral-500">Nombre del Hotel:</span>
                  <span className="text-white font-medium">{formData.hotelName}</span>
                </div>
                <div className="flex justify-between border-b border-neutral-800/50 pb-2">
                  <span className="text-neutral-500">Administrador:</span>
                  <span className="text-white font-medium">{formData.fullName}</span>
                </div>
                <div className="flex justify-between border-b border-neutral-800/50 pb-2">
                  <span className="text-neutral-500">Email de Contacto:</span>
                  <span className="text-white font-medium">{formData.email}</span>
                </div>
              </div>
              <div className="space-y-4">
                <h3 className="text-lg text-white font-medium border-b border-neutral-800 pb-2">Parámetros Financieros</h3>
                <div className="flex justify-between border-b border-neutral-800/50 pb-2">
                  <span className="text-neutral-500">Moneda Base:</span>
                  <span className="text-white font-medium">{formData.currency}</span>
                </div>
                <div className="flex justify-between border-b border-neutral-800/50 pb-2">
                  <span className="text-neutral-500">Estado de Cuenta:</span>
                  <span className="text-amber-500 font-medium">Trial Activo (14 días)</span>
                </div>
              </div>
            </div>
          </div>
        );
      case 'panel':
      default:
        return (
          <div className="animate-fade-in">
            {/* Welcome Banner */}
            <div className="bg-gradient-to-r from-neutral-900 to-[#1a1505] border border-amber-500/20 rounded-2xl p-8 mb-8 relative overflow-hidden">
              <div className="absolute right-0 top-0 opacity-10">
                <Building2 size={200} className="text-amber-500" />
              </div>
              <div className="relative z-10 max-w-2xl">
                <div className="inline-flex items-center bg-amber-500/10 text-amber-500 text-xs font-bold px-3 py-1 rounded-full mb-4 border border-amber-500/20">
                  <Sparkles size={14} className="mr-1" /> Entorno Listo
                </div>
                <h2 className="text-3xl font-light text-white mb-2">
                  Su plataforma para <span className="font-bold">{formData.hotelName || 'su hotel'}</span> está en línea.
                </h2>
                <p className="text-neutral-400 text-lg mb-8">Hemos precargado los beneficios del modelo <span className="text-white font-medium">{formData.template}</span> en su base de datos aislada. Está listo para fidelizar.</p>
                
                {/* NUEVO BOTÓN PUENTE AL SISTEMA REAL */}
                <button 
                  onClick={() => window.location.href = `/?hotel=${formData.hotelName.toLowerCase().replace(/[^a-z0-9]/g, '-')}&preview=true`}
                  className="bg-amber-500 hover:bg-amber-400 text-neutral-950 font-bold py-4 px-8 rounded-xl transition-all shadow-[0_0_30px_rgba(245,158,11,0.4)] hover:scale-105 active:scale-95 flex items-center gap-3"
                >
                  Ir a mi Hub Operativo Real <ArrowRight size={20} />
                </button>
              </div>
            </div>

            {/* Checklist */}
            <h3 className="text-xl font-medium text-white mb-4">Guía de Inicio Rápido</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div onClick={() => setDashboardTab('configuracion')} className="bg-neutral-900/50 border border-neutral-800 rounded-xl p-6 relative overflow-hidden cursor-pointer hover:border-neutral-600 transition-colors">
                <div className="absolute top-0 right-0 p-4"><Check size={24} className="text-amber-500" /></div>
                <div className="w-12 h-12 bg-amber-500/10 rounded-lg flex items-center justify-center mb-4 text-amber-500">
                  <Upload size={24} />
                </div>
                <h4 className="text-white font-medium mb-2">1. Identidad Visual</h4>
                <p className="text-sm text-neutral-400">Logo y colores corporativos configurados correctamente.</p>
              </div>

              <div onClick={() => setDashboardTab('catalogo')} className="bg-[#111] border border-neutral-700 hover:border-amber-500/50 transition-colors rounded-xl p-6 cursor-pointer group">
                <div className="absolute top-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity"><ArrowRight size={20} className="text-amber-500" /></div>
                <div className="w-12 h-12 bg-neutral-800 rounded-lg flex items-center justify-center mb-4 text-neutral-400 group-hover:text-amber-500 transition-colors">
                  <Gift size={24} />
                </div>
                <h4 className="text-white font-medium mb-2">2. Revisar Beneficios</h4>
                <p className="text-sm text-neutral-400">Edite los costos en puntos o las imágenes de su catálogo inicial.</p>
              </div>

              <div onClick={() => setDashboardTab('crm')} className="bg-[#111] border border-neutral-700 hover:border-amber-500/50 transition-colors rounded-xl p-6 cursor-pointer group">
                 <div className="absolute top-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity"><ArrowRight size={20} className="text-amber-500" /></div>
                <div className="w-12 h-12 bg-neutral-800 rounded-lg flex items-center justify-center mb-4 text-neutral-400 group-hover:text-amber-500 transition-colors">
                  <Users size={24} />
                </div>
                <h4 className="text-white font-medium mb-2">3. Invitar Huésped de Prueba</h4>
                <p className="text-sm text-neutral-400">Envíe un acceso a su propio móvil para vivir la experiencia.</p>
              </div>
            </div>
          </div>
        );
    }
  };

  const renderDashboard = () => (
    <div className="animate-fade-in w-full h-screen fixed inset-0 bg-[#0a0a0a] z-50 flex">
      {/* Sidebar Mockup - OCULTO EN MÓVILES (hidden md:flex) */}
      <div className="hidden md:flex w-64 bg-[#111] border-r border-neutral-800 p-6 flex-col shrink-0">
        <div className="mb-8 h-12 flex items-center">
          {formData.logoUrl !== 'https://images.paxfidely.com/placeholders/logo-default.png' ? (
             <img src={formData.logoUrl} alt="Hotel Logo" className="max-h-full object-contain" />
          ) : (
             <div className="text-amber-500 font-bold text-xl tracking-widest uppercase">
              PAX<span className="text-white">FIDELY</span>
             </div>
          )}
        </div>

        <nav className="flex-1 space-y-2">
          <button 
            onClick={() => setDashboardTab('panel')} 
            className={`w-full flex items-center px-4 py-3 rounded-lg text-sm font-medium transition-colors ${dashboardTab === 'panel' ? 'text-amber-500 bg-amber-500/10' : 'text-neutral-400 hover:text-white hover:bg-neutral-800/50'}`}
          >
            <LayoutDashboard size={18} className="mr-3" /> Panel Principal
          </button>
          
          <button 
            onClick={() => setDashboardTab('crm')} 
            className={`w-full flex items-center px-4 py-3 rounded-lg text-sm font-medium transition-colors ${dashboardTab === 'crm' ? 'text-amber-500 bg-amber-500/10' : 'text-neutral-400 hover:text-white hover:bg-neutral-800/50'}`}
          >
            <Users size={18} className="mr-3" /> Socios / CRM
          </button>
          
          <button 
            onClick={() => setDashboardTab('catalogo')} 
            className={`w-full flex items-center px-4 py-3 rounded-lg text-sm font-medium transition-colors ${dashboardTab === 'catalogo' ? 'text-amber-500 bg-amber-500/10' : 'text-neutral-400 hover:text-white hover:bg-neutral-800/50'}`}
          >
            <Gift size={18} className="mr-3" /> Catálogo Beneficios
          </button>
          
          <button 
            onClick={() => setDashboardTab('configuracion')} 
            className={`w-full flex items-center px-4 py-3 rounded-lg text-sm font-medium transition-colors ${dashboardTab === 'configuracion' ? 'text-amber-500 bg-amber-500/10' : 'text-neutral-400 hover:text-white hover:bg-neutral-800/50'}`}
          >
            <Settings size={18} className="mr-3" /> Configuración
          </button>
        </nav>
        
        <div className="mt-auto bg-neutral-900 p-4 rounded-xl border border-neutral-800">
           <div className="text-xs text-neutral-500 mb-1">Estado</div>
           <div className="text-white font-bold text-sm flex items-center mb-2"><Crown size={14} className="text-amber-500 mr-1" /> Trial Activo</div>
           <div className="w-full bg-neutral-800 rounded-full h-1.5 mb-2">
             <div className="bg-amber-500 h-1.5 rounded-full" style={{ width: '7%' }}></div>
           </div>
           <div className="text-xs text-neutral-400">Día 1 de 14</div>
        </div>
      </div>

      {/* Main Content Mockup */}
      <div className="flex-1 flex flex-col overflow-y-auto w-full">
        {/* Topbar Responsive */}
        <header className="h-16 md:h-20 border-b border-neutral-800 flex items-center justify-between px-4 md:px-10 bg-[#0a0a0a]/80 backdrop-blur-md sticky top-0 z-10">
          
          {/* Logo visible solo en móvil */}
          <div className="md:hidden flex items-center h-7 mr-2 shrink-0">
            {formData.logoUrl !== 'https://images.paxfidely.com/placeholders/logo-default.png' ? (
               <img src={formData.logoUrl} alt="Hotel Logo" className="max-h-full object-contain" />
            ) : (
               <div className="text-amber-500 font-bold text-xs tracking-widest uppercase">
                PAX<span className="text-white">FIDELY</span>
               </div>
            )}
          </div>

          <h1 className="hidden sm:block text-lg md:text-xl font-light text-white truncate">
            Bienvenido, <span className="font-bold">{formData.fullName.split(' ')[0] || 'Admin'}</span>
          </h1>
          
          <div className="flex items-center gap-3 ml-auto shrink-0">
             <span className="hidden md:inline-block text-sm text-neutral-400 border border-neutral-800 px-3 py-1 rounded-full">
               Simulador
             </span>
             <button 
                onClick={() => window.location.href = `/?hotel=${formData.hotelName.toLowerCase().replace(/[^a-z0-9]/g, '-')}&preview=true`}
                className="bg-amber-500 hover:bg-amber-400 text-neutral-950 px-4 py-2 md:px-5 md:py-2.5 text-xs md:text-sm font-bold rounded-lg transition-all shadow-lg hover:scale-105 whitespace-nowrap"
             >
                Acceder al Hub Real
             </button>
          </div>
        </header>

        {/* Content */}
        <main className="p-4 md:p-10 flex-1">
          {renderDashboardContent()}
        </main>
      </div>
    </div>
  );

  return (
    <div className="h-screen w-full overflow-y-auto overflow-x-hidden bg-[#0a0a0a] font-sans selection:bg-amber-500/30 relative">
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-amber-500/5 rounded-full blur-[120px] pointer-events-none z-0"></div>
      
      {step < 3 && (
        <div className="relative z-10 min-h-full flex flex-col">
          <header className="px-6 py-6 border-b border-neutral-900/80 w-full flex justify-between items-center bg-[#0a0a0a]">
            <div className="text-amber-500 font-bold text-xl tracking-widest uppercase">
              PAX<span className="text-white">FIDELY</span>
            </div>
            <div className="text-neutral-500 text-sm flex items-center">
              <Lock size={14} className="mr-1" /> Conexión Segura SSL
            </div>
          </header>

          <main className="flex-1 w-full px-6 py-12 pb-24">
            <div className="max-w-4xl mx-auto w-full">
              {renderProgressBar()}
              {step === 1 && renderStep1()}
              {step === 2 && renderStep2()}
              {/* El Paso 3 (Planes) fue eliminado estratégicamente */}
            </div>
          </main>
        </div>
      )}

      {step === 3 && renderDashboard()}
    </div>
  );
}